
import React, { useState, useMemo } from "react";
import styles from "./intakescreen.module.css";
import WOMEN_HEALTH from "../../womenhealth.json";
import { INTAKE_QUESTIONS } from "./Lifestage";

/* ── Map entry reason value → intakeKey ─────────────────────────── */
// Pull intakeKey from womenhealth.json entryReasons options
const buildIntakeKeyMap = () => {
  const map = {};
  const { entryReasons } = WOMEN_HEALTH;
  Object.values(entryReasons || {}).forEach((journey) => {
    (journey.options || []).forEach((opt) => {
      if (opt.intakeKey) map[opt.value] = opt.intakeKey;
    });
  });
  return map;
};

// Fallback map in case womenhealth.json options don't have intakeKey yet
const FALLBACK_INTAKE_MAP = {
  period_problem:     "menstrual_health",
  acne_hair:          "pcos_screening",
  pcos:               "pcos_screening",
  pcos_thyroid:       "pcos_screening",
  pelvic_pain:        "pelvic_pain",
  puberty_guidance:   "puberty_education",
  mental_wellness:    "mental_wellbeing",
  mood:               "mental_wellbeing",
  mood_sleep:         "mental_wellbeing",
  checkup:            "preventive_care",
  doctor:             "doctor_booking",
  pregnancy_maybe:    "early_pregnancy",
  maybe_pregnant:     "early_pregnancy",
  plan_pregnancy:     "preconception",
  trying_conceive:    "fertility",
  contraception:      "contraception",
  infection:          "infection_gynae",
  irregular_periods:  "menstrual_health",
  miscarriage:        "post_miscarriage",
  fertility_tests:    "fertility",
  lifestyle:          "lifestyle",
  menopause_symptoms: "menopause_symptoms",
  heavy_bleeding:     "heavy_bleeding",
  vaginal_dryness:    "urogenital",
  bone_heart:         "bone_health",
  bone:               "bone_health",
  breast:             "preventive_care",
  postmenopause_bleed:"heavy_bleeding",
  chronic:            "chronic_disease",
  menopause_wellness: "menopause_symptoms",
  pelvic_floor:       "urogenital",
};

const resolveIntakeKey = (value, dynamicMap) =>
  dynamicMap[value] || FALLBACK_INTAKE_MAP[value] || "preventive_care";

/* ── Progress bar helper ─────────────────────────────────────────── */
const ProgressBar = ({ current, total, label }) => (
  <div className={styles.progressWrap}>
    <div className={styles.progressHeader}>
      <span className={`ava-text-caption ${styles.progressLabel}`}>{label}</span>
      <span className={`ava-text-caption ${styles.progressCount}`}>
        {current + 1} / {total}
      </span>
    </div>
    <div className={styles.progressTrack}>
      <div
        className={styles.progressFill}
        style={{ width: `${((current + 1) / total) * 100}%` }}
      />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────── */
const IntakeScreen = ({ entryPayload, patientData = {}, onComplete, onBack }) => {
  const {
    reasons = [],
    options: entryOptions = [],
    journeyTitle = "",
  } = entryPayload || {};

  /* Build intake groups: one per selected reason */
  const intakeDynamicMap = useMemo(() => buildIntakeKeyMap(), []);

  const groups = useMemo(() => {
    return reasons
      .map((reasonValue) => {
        const entryOpt  = entryOptions.find((o) => o.value === reasonValue);
        const intakeKey = resolveIntakeKey(reasonValue, intakeDynamicMap);
        const intake    = INTAKE_QUESTIONS[intakeKey];
        if (!intake) return null;
        return {
          reasonValue,
          reasonLabel: entryOpt?.label || reasonValue,
          intakeKey,
          ...intake,  // { title, emoji, questions[] }
        };
      })
      .filter(Boolean);
  }, [reasons, entryOptions, intakeDynamicMap]);

  /* Navigation state */
  const [groupIdx,  setGroupIdx]  = useState(0);  // which reason group we're on
  const [qIdx,      setQIdx]      = useState(0);  // which question within group
  const [allAnswers, setAllAnswers] = useState({}); // { reasonValue: { qId: answer } }
  const [qAnswers,  setQAnswers]  = useState({});  // answers for current group

  /* Current group + question */
  const group   = groups[groupIdx];
  const question = group?.questions?.[qIdx];
  const isLast  = groupIdx === groups.length - 1 && qIdx === (group?.questions?.length || 1) - 1;
  const isMulti = question?.type === "multi";

  /* Current answer state for this question */
  const currentAnswer = qAnswers[question?.id] ?? (isMulti ? [] : null);

  /* ── Option select handler ─── */
  const handleSelect = (value) => {
    if (isMulti) {
      const prev = Array.isArray(currentAnswer) ? currentAnswer : [];
      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      setQAnswers((p) => ({ ...p, [question.id]: next }));
    } else {
      // single select: auto-advance after brief delay
      setQAnswers((p) => ({ ...p, [question.id]: value }));
      setTimeout(() => advanceQuestion({ ...qAnswers, [question.id]: value }), 260);
    }
  };

  /* ── Advance question / group ─── */
  const advanceQuestion = (latestQAnswers = qAnswers) => {
    const nextQIdx = qIdx + 1;

    if (nextQIdx < group.questions.length) {
      // Next question in same group
      setQIdx(nextQIdx);
    } else {
      // Save this group's answers
      const merged = {
        ...allAnswers,
        [group.reasonValue]: latestQAnswers,
      };
      setAllAnswers(merged);
      setQAnswers({});

      const nextGroupIdx = groupIdx + 1;
      if (nextGroupIdx < groups.length) {
        // Move to next reason group
        setGroupIdx(nextGroupIdx);
        setQIdx(0);
      } else {
        // All groups done — save to localStorage and finish
        try {
          const existing = JSON.parse(localStorage.getItem("patientPersona") || "{}");
          localStorage.setItem("patientPersona", JSON.stringify({
            ...existing,
            intakeAnswers: merged,
            savedAt: Date.now(),
          }));
        } catch (e) { /* ignore */ }
        onComplete?.(merged);
      }
    }
  };

  /* ── Back handler ─── */
  const handleBack = () => {
    if (qIdx > 0) {
      // Go back one question in current group
      setQIdx((p) => p - 1);
      // Remove last answer
      const prevId = group.questions[qIdx - 1]?.id;
      if (prevId) {
        setQAnswers((p) => {
          const next = { ...p };
          delete next[group.questions[qIdx].id];
          return next;
        });
      }
    } else if (groupIdx > 0) {
      // Go back to last question of previous group
      const prevGroup = groups[groupIdx - 1];
      const prevGroupAnswers = allAnswers[prevGroup.reasonValue] || {};
      setGroupIdx((p) => p - 1);
      setQIdx(prevGroup.questions.length - 1);
      setQAnswers(prevGroupAnswers);
      setAllAnswers((p) => {
        const next = { ...p };
        delete next[prevGroup.reasonValue];
        return next;
      });
    } else {
      // Back to EntryReasonScreen
      onBack?.();
    }
  };

  /* ── Edge: no groups resolved ─── */
  if (groups.length === 0) {
    return (
      <div className={styles.wrapper}>
        <p className={styles.emptyMsg}>No questionnaire found for your selection.</p>
        <button type="button" className={styles.backBtn} onClick={onBack}>
          ← Go Back
        </button>
      </div>
    );
  }

  const totalQuestionsInGroup = group?.questions?.length || 1;
  const hasAnswer = isMulti
    ? Array.isArray(currentAnswer) && currentAnswer.length > 0
    : currentAnswer !== null;

  return (
    <div className={styles.wrapper}>
      {/* ── Top progress: which reason group ── */}
      {groups.length > 1 && (
        <div className={styles.groupTabs}>
          {groups.map((g, i) => (
            <div
              key={g.reasonValue}
              className={`${styles.groupTab} ${
                i === groupIdx
                  ? styles.groupTabActive
                  : i < groupIdx
                  ? styles.groupTabDone
                  : ""
              }`}
            >
              <span className={styles.groupTabEmoji}>{g.emoji}</span>
              <span className={`ava-text-caption ${styles.groupTabLabel}`}>
                {g.title}
              </span>
              {i < groupIdx && <span className={styles.groupTabCheck}>✓</span>}
            </div>
          ))}
        </div>
      )}

      {/* ── Question progress bar ── */}
      <ProgressBar
        current={qIdx}
        total={totalQuestionsInGroup}
        label={`${group?.emoji || ""} ${group?.title || ""}`}
      />

      {/* ── Question ── */}
      <div className={styles.questionArea}>
        {/* Journey context pill */}
        <span className={styles.journeyPill}>{journeyTitle}</span>

        <h2 className={`ava-heading-md ${styles.question}`}>
          {question?.q}
        </h2>

        {isMulti && (
          <p className={`ava-text-caption ${styles.multiHint}`}>
            Select all that apply
          </p>
        )}
      </div>

    {/* ── Options ── */}
<div className={styles.options}>
  {(question?.options || []).map((opt) => {
    const isSel = isMulti
      ? Array.isArray(currentAnswer) && currentAnswer.includes(opt)
      : currentAnswer === opt;

    return (
      <button
        key={opt}
        type="button"
        className={`${styles.optBtn} ${isSel ? styles.optSelected : ""}`}
        onClick={() => handleSelect(opt)}
      >
        <span className={`ava-text-body ${styles.optLabel}`}>{opt}</span>
      </button>
    );
  })}
</div>

      {/* ── Action row ── */}
     <div className="flex items-center justify-between w-full mt-1">
  <button
    type="button"
    className={styles.backBtn}
    onClick={handleBack}
  >
    ← Back
  </button>

  {/* For multi-select, show an explicit Next button */}
  {isMulti && (
    <button
      type="button"
      className={`btn-primary `}
      disabled={!hasAnswer}
      onClick={() => advanceQuestion()}
    >
      {isLast ? "Finish" : "Next →"}
    </button>
  )}

  {/* For single-select and last question, show explicit Next */}
  {!isMulti && isLast && (
    <button
      type="button"
      className={`btn-primary `}
      disabled={!hasAnswer}
      onClick={() => advanceQuestion()}
    >
      Finish
    </button>
  )}
</div>

      {/* Secure note */}
      {/* <p className={`ava-text-caption ${styles.note}`}>
        Your information is private and secure 🔒
      </p> */}
    </div>
  );
};

export default IntakeScreen;